import {
  CellClickedEvent,
  ColDef,
  GridOptions,
  RowStyle,
  ValueFormatterParams,
} from '@ag-grid-community/core';

import { genericGetAttributeColumns } from '@features/budget-attributes/attribute-columns';

import { rawAttributeColumnRenderer } from '@shared/components/ag-raw-attribute-renderer/ag-raw-attribute-renderer';
import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { TableConstants } from '@shared/constants/table.constants';
import { Currency, listJournalEntriesQuery } from '@shared/services/gql.service';
import { GlobalState } from '@shared/store/global/global.model';
import { CurrencyToggle } from '@shared/types/components.type';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

import { JournalEntriesGridRow } from './journal-entries.model';

type JournalEntriesConfigContext = {
  attributeConfig: Pick<GlobalState, 'attributes' | 'primaryAttribute' | 'secondaryAttribute'> & {
    gridData: JournalEntriesGridRow[];
  };

  currencyToggle: `${CurrencyToggle}`;
  redirectFn: (params: CellClickedEvent) => void;
};

const DEFAULT_JOURNAL_ENTRIES_GRID_OPTIONS: GridOptions<JournalEntriesGridRow> = {
  columnDefs: [],
  defaultColDef: {
    ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    resizable: true,
    sortable: false,
    tooltipComponent: AgRawTooltip,
    valueFormatter: (params: ValueFormatterParams) => params.value || ZERO_HYPHEN,
  },
  getRowHeight: (params) => {
    if (params.node.isRowPinned()) {
      return 48;
    }

    return 40;
  },
  getRowStyle: (params) => {
    if (params.node.isRowPinned()) {
      return <RowStyle>{ '--ag-row-height': '48px' };
    }

    return {};
  },
  headerHeight: 48,
  isExternalFilterPresent: () => true,
  rowHeight: 40,
  suppressAggFuncInHeader: true,
  suppressCellFocus: true,
  suppressRowClickSelection: true,
  suppressRowTransform: true,
  tooltipShowDelay: 500,
};

export const journalEntriesGridOptions = (ctx: JournalEntriesConfigContext) => {
  const isPivotSelected = !!ctx.attributeConfig.primaryAttribute.attribute_name;
  const isSecondaryAttributeSelected = !!ctx.attributeConfig.secondaryAttribute.attribute_name;
  const costCategoryName = 'Cost Category' + (isPivotSelected ? '' : ' *');

  const costCategoryColumn: ColDef = {
    field: getEncodedAttributeName('COST_CATEGORY'),
    headerClass: ['text-center'],
    headerName: costCategoryName,
    pinned: isPivotSelected ? undefined : 'left',
    ...rawAttributeColumnRenderer(
      ctx.attributeConfig,
      costCategoryName,
      isSecondaryAttributeSelected ? 2 : isPivotSelected ? 1 : 0
    ),
  };

  const otherAttributes = genericGetAttributeColumns({
    attributeConfig: ctx.attributeConfig,
    colDefOverrides: {
      headerClass: ['text-center'],
    },
    offset: 1,
    onlyPivot: null,
  });

  if (isPivotSelected) {
    otherAttributes.push(costCategoryColumn);
    otherAttributes.sort((a, b) =>
      Utils.localeAlphaNumSort(a.headerName || '', b.headerName || '')
    );
  }

  return <GridOptions<JournalEntriesGridRow>>{
    ...DEFAULT_JOURNAL_ENTRIES_GRID_OPTIONS,
    columnDefs: [
      getPinnedColumns(ctx),
      {
        field: 'entry_name',
        headerClass: ['text-center'],
        headerName: 'Account Description',
        minWidth: 200,
        pinned: 'left',
        width: 200,
      },
      ...genericGetAttributeColumns({
        attributeConfig: ctx.attributeConfig,
        colDefOverrides: {
          headerClass: ['text-center'],
          pinned: 'left',
        },
        onlyPivot: true,
      }),
      ...(isPivotSelected ? [] : [costCategoryColumn]),
      ...otherAttributes,
      ...getDefaultColumns(ctx),
    ],
  };
};

export const getJournalEntriesGridData = (
  data: listJournalEntriesQuery[],
  currencyToggle: `${CurrencyToggle}`
): JournalEntriesGridRow[] => {
  return data.map((row) => {
    const attributes = JSON.parse(
      JSON.parse(row.attributes || '"[]"')
    ) as JournalEntriesGridRow['attributes'];
    const groupValues = JSON.parse(JSON.parse(row.group_value || '"{}"'));
    const extraAttributes =
      attributes.reduce(
        (acc, a) => {
          if (a.attribute_name && a.attribute_value) {
            acc[getEncodedAttributeName(a.attribute_name)] = a.attribute_value;
          }

          return acc;
        },
        {} as Record<string, string>
      ) || {};

    const groupAttributes = Object.keys(groupValues).reduce(
      (acc, key) => {
        acc[getEncodedAttributeName(key)] = groupValues[key];

        return acc;
      },
      {} as Record<string, string>
    );

    const attributeObj = {
      ...extraAttributes,
      ...groupAttributes,
    };

    const isCategory = !!attributeObj[getEncodedAttributeName('COST_CATEGORY')];
    const isContracted = currencyToggle === CurrencyToggle.CONTRACTED;

    return {
      attributes,
      credit_amount: isContracted ? row.credit_contracted_amount : row.credit_amount,
      debit_amount: isContracted ? row.debit_contracted_amount : row.debit_amount,
      entry_name:
        row.entry_name === 'ACTIVITY_NONE' || isCategory
          ? `Expense ${row.entry_type.includes('_PREPAID') ? '(Prepaid)' : ''}`
          : row.entry_name,
      entry_type: row.entry_type,
      line_memo: row?.line_memo,
      program: row.program,
      project: row.project,
      reversal_date: row.reversal_date,
      sponsor_name: row.sponsor_name,
      vendor_currency: isContracted
        ? (row?.vendor_currency?.replace('CURRENCY_', '') as Currency) || Currency.USD
        : Currency.USD,
      vendor_id: row.vendor_id,
      vendor_name: row.vendor_name,
      ...attributeObj,
    };
  });
};

const getPinnedColumns = (_ctx: JournalEntriesConfigContext): ColDef => {
  return {
    field: 'vendor_id',
    headerClass: ['text-center'],
    headerName: 'Vendor Contract',
    minWidth: 230,
    pinned: 'left',
    resizable: false,
    valueFormatter: (params: ValueFormatterParams<JournalEntriesGridRow>) => {
      if (params.node?.isRowPinned()) {
        return 'Total';
      }

      return params.data?.vendor_name || '';
    },
    width: 230,
    // ...getVendorRowSpanConfig<JournalEntriesGridRow>('vendor_id'),
  };
};

const getDefaultColumns = (ctx: JournalEntriesConfigContext): ColDef[] => {
  return [
    ...getDebitCreditColumns(ctx),
    {
      field: 'vendor_currency',
      headerClass: ['text-center'],
      headerName: 'Currency',
      minWidth: 90,
      width: 90,
    },
    {
      cellClass: ['!block', 'truncate', 'text-left'],
      field: 'line_memo',
      headerClass: ['text-center'],
      headerName: 'Line Memo',
      minWidth: 125,
      tooltipField: 'line_memo',
      width: 125,
    },
    {
      cellClass: ['!block', 'truncate', 'text-left'],
      field: 'sponsor_name',
      headerClass: ['text-center'],
      headerName: 'Subsidiary',
      minWidth: 150,
      tooltipField: 'sponsor_name',
      width: 150,
    },
    {
      cellClass: ['!block', 'truncate', 'text-left'],
      field: 'program',
      headerClass: ['text-center'],
      headerName: 'Program',
      minWidth: 150,
      tooltipField: 'program',
      width: 150,
    },
    {
      cellClass: ['!block', 'truncate', 'text-left'],
      field: 'project',
      headerClass: ['text-center'],
      headerName: 'Project',
      minWidth: 150,
      tooltipField: 'project',
      width: 150,
    },
    {
      cellClass: ['text-right'],
      field: 'reversal_date',
      headerClass: ['text-center'],
      headerName: 'Reversal Date',
      minWidth: 150,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return Utils.agDateFormatter(params, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        }

        return '';
      },
      width: 150,
    },
  ];
};

const currencyFormatter = (params: ValueFormatterParams<JournalEntriesGridRow>) =>
  Utils.agCurrencyFormatterAccounting(params, params.data?.vendor_currency || Currency.USD);

const getDebitCreditColumns = (ctx: JournalEntriesConfigContext): ColDef[] => {
  return [
    {
      cellClass: (value) => {
        const classes: string[] = ['text-right'];

        if (value.value) {
          classes.push('cursor-pointer text-aux-blue underline');
        } else {
          classes.push('aux-link-zero-hyphen');
        }

        return classes;
      },
      field: 'debit_amount',
      headerClass: ['text-center'],
      headerName: 'Debit',
      minWidth: 125,
      onCellClicked: (event: CellClickedEvent) => {
        if (event.value) {
          ctx.redirectFn(event);
        }
      },
      valueFormatter: currencyFormatter,
      width: 125,
    },
    {
      cellClass: (value) => {
        const classes: string[] = ['text-right'];

        if (value.value) {
          classes.push('cursor-pointer text-aux-blue underline');
        } else {
          classes.push('aux-link-zero-hyphen');
        }

        return classes;
      },
      field: 'credit_amount',
      headerClass: ['text-center'],
      headerName: 'Credit',
      minWidth: 125,
      onCellClicked: (event: CellClickedEvent) => {
        if (event.value) {
          ctx.redirectFn(event);
        }
      },
      valueFormatter: currencyFormatter,
      width: 125,
    },
  ];
};
